import { useEffect, useRef, useState } from "react";
import { getZipInfo } from "../../services/mediaService";
import { type DownloadMediaItemsCallback } from "./DownloadMediaItems";
import { Loader } from "../loader";
import { useTranslations } from "next-intl";
import { useAppConfiguration } from "appConfig";
import { BsXLg } from "react-icons/bs";
import { generateIcon } from "utils";
import Link from "next/link";

export interface ZipDownloaderProps {
  zipId: string;
  fileName: string;
  callback?: DownloadMediaItemsCallback;
  guestToken?: string | null;
}

export function ZipDownloader({
  zipId,
  fileName,
  callback,
  guestToken,
}: ZipDownloaderProps) {
  const [isActive, setIsActive] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const t = useTranslations();

  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);
  const { apiUrl } = useAppConfiguration();

  useEffect(() => {
    isMounted.current = true;

    const pollZipProgress = async () => {
      const res = await getZipInfo(zipId, guestToken ?? undefined);
      if (!isMounted.current) return;

      if ((res?.progress ?? 0) < 100) {
        saveTimeout.current = setTimeout(pollZipProgress, 5000);
        return;
      }

      setIsActive(false);
    };

    void pollZipProgress();

    // Without this the poll keeps running after the component is gone.
    return () => {
      isMounted.current = false;
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
        saveTimeout.current = null;
      }
    };
  }, [zipId, guestToken]);

  if (isDismissed) return null;

  return (
    <div className="card bg-base-200 w-full rounded-box py-3 px-5 shadow-lg my-2 flex flex-row justify-between">
      {isActive ? (
        <div className="flex flex-row items-center justify-center">
          <div className="w-7 h-7 ">
            <Loader />
          </div>
          <span className="ml-2">{`${t("Gallery.zipBuildInProgress")} ${fileName}`}</span>
        </div>
      ) : (
        <Link
          href={`${apiUrl}/api/media/downloadZip/${zipId}`}
          className="btn btn-primary"
          target="_blank"
        >
          {t("Gallery.downloadZip")}
        </Link>
      )}
      <span
        onClick={() => setIsDismissed(true)}
        className="block"
        title={t("Buttons.close")}
      >
        {generateIcon(BsXLg, "text-[22px] cursor-pointer")}
      </span>
    </div>
  );
}
